// Data behind the Roles & Permissions screen. Mirrors the server's guards
// (common/auth/guards.ts + PLATFORM.tenancy.ownRecordsOnlyRoles) — documentation,
// not enforcement, so it is wrong until updated when a guard changes.
// Keep in step with mobileapp/src/config/permissions.ts.
//
// "platform owner" and "broker" are excluded: neither is an organization role.

export type RoleId = "owner" | "admin" | "manager" | "employee";

export const ROLE_IDS: RoleId[] = ["owner", "admin", "manager", "employee"];

/** How much of the organization's data this role can see. */
export type Visibility = "all" | "own";

export interface RoleProfile {
  id: RoleId;
  name: string;
  summary: string;
  visibility: Visibility;
}

export const ROLE_PROFILES: Record<RoleId, RoleProfile> = {
  owner: {
    id: "owner",
    name: "Owner",
    summary:
      "Runs the organization. Full access to everything, and the only role that can manage the subscription and billing.",
    visibility: "all",
  },
  admin: {
    id: "admin",
    name: "Admin",
    summary:
      "Manages day-to-day operations and the team. Same access as the Owner across the organization's data, but cannot manage billing.",
    visibility: "all",
  },
  manager: {
    id: "manager",
    name: "Manager",
    summary:
      "Works with the records they create. Full add, edit and delete rights, but sees only their own records — plus stock, which is shared.",
    visibility: "own",
  },
  employee: {
    id: "employee",
    name: "Employee",
    summary:
      "Same access as a Manager: full rights over their own records, and shared visibility of stock. The two differ only by title today.",
    visibility: "own",
  },
};

export const VISIBILITY_TEXT: Record<Visibility, string> = {
  all: "Everything in this organization, whoever created it.",
  own: "Only the records you created yourself. Records added by other people are hidden entirely — they will not appear in lists or searches.",
};

export interface Capability {
  id: string;
  label: string;
  /** Roles the server lets through at all. */
  allow: RoleId[];
  /** Allowed, but limited to records they created. */
  ownOnly?: RoleId[];
  /** Overrides the default own-records qualifier. */
  qualifier?: string;
  note?: string;
}

export interface CapabilitySection {
  id: string;
  title: string;
  capabilities: Capability[];
}

export const OWN_ONLY_QUALIFIER = "Only the records you created";

const ALL: RoleId[] = ["owner", "admin", "manager", "employee"];
const ADMINS: RoleId[] = ["owner", "admin"];
const OWN: RoleId[] = ["manager", "employee"];

export const CAPABILITY_SECTIONS: CapabilitySection[] = [
  {
    // On the web these are genuinely gated: each role gets its own module from
    // module-config.tsx, so a screen outside it has no route at all.
    id: "screens",
    title: "Screens you can open",
    capabilities: [
      { id: "s-dashboard", label: "Dashboard", allow: ALL },
      { id: "s-products", label: "Products", allow: ALL, ownOnly: OWN },
      {
        id: "s-catalog",
        label: "Catalog (brands, categories, suppliers, warehouses)",
        allow: ALL,
        ownOnly: OWN,
      },
      {
        id: "s-orders",
        label: "Orders, sales and invoices",
        allow: ALL,
        ownOnly: OWN,
      },
      {
        id: "s-team",
        label: "Managers and Employees",
        allow: ADMINS,
        note: "The team screens live in the Owner and Admin modules only.",
      },
      {
        id: "s-subscription",
        label: "Subscription & billing",
        allow: ["owner"],
        note: "In the Owner module only — no other role has the route.",
      },
      { id: "s-gallery", label: "Gallery", allow: ADMINS },
      { id: "s-organizations", label: "My Organizations", allow: ALL },
      { id: "s-profile", label: "Profile", allow: ALL },
    ],
  },
  {
    id: "catalog",
    title: "Products & catalog",
    capabilities: [
      // Domain modules guard with resolveScope only — never requireOrgAdmin.
      {
        id: "view-catalog",
        label:
          "View products, brands, categories, suppliers, warehouses and customers",
        allow: ALL,
        ownOnly: OWN,
      },
      { id: "edit-catalog", label: "Add and edit them", allow: ALL, ownOnly: OWN },
      {
        id: "delete-catalog",
        label: "Delete them",
        allow: ALL,
        ownOnly: OWN,
        note: "Deleted items go to Trash first, not straight out.",
      },
    ],
  },
  {
    id: "stock",
    title: "Stock",
    capabilities: [
      // stock-level/adjustment use their own orgScopeFilter, omitting
      // restrictToOwnCreated — these rows have no createdBy ownership.
      {
        id: "view-stock",
        label: "View stock levels across warehouses",
        allow: ALL,
        note: "Stock is shared: everyone sees the whole picture, whoever recorded it.",
      },
      { id: "adjust-stock", label: "Record stock adjustments", allow: ALL },
    ],
  },
  {
    id: "trade",
    title: "Sales & purchasing",
    capabilities: [
      {
        id: "orders",
        label: "Create and manage purchase and sales orders",
        allow: ALL,
        ownOnly: OWN,
      },
      { id: "invoices", label: "Create and manage invoices", allow: ALL, ownOnly: OWN },
      { id: "payments", label: "Record payments", allow: ALL, ownOnly: OWN },
    ],
  },
  {
    id: "team",
    title: "Team",
    capabilities: [
      { id: "view-team", label: "View the team list", allow: ADMINS },
      { id: "manage-team", label: "Invite, edit and remove team members", allow: ADMINS },
    ],
  },
  {
    id: "organization",
    title: "Organization",
    capabilities: [
      { id: "trash", label: "Restore or permanently delete items in Trash", allow: ADMINS },
      {
        id: "layouts",
        label: "Create shared table layouts for the organization",
        allow: ADMINS,
        note: "Everyone can use shared layouts — only these roles can create them.",
      },
      { id: "billing", label: "Manage the subscription and billing", allow: ["owner"] },
    ],
  },
  {
    id: "personal",
    title: "Your account",
    capabilities: [
      { id: "profile", label: "Edit your own profile", allow: ALL },
      { id: "delete-account", label: "Delete your own account", allow: ALL },
    ],
  },
];

export type CapabilityLevel = "full" | "own" | "none";

export const levelFor = (capability: Capability, role: RoleId): CapabilityLevel => {
  if (!capability.allow.includes(role)) return "none";
  return capability.ownOnly?.includes(role) ? "own" : "full";
};
